/**
 * Color utility functions for business theme colors
 */

/**
 * Convert hex color to RGB
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null
}

/**
 * Convert RGB to hex
 */
function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map((x) => {
    const hex = x.toString(16)
    return hex.length === 1 ? '0' + hex : hex
  }).join('')
}

/**
 * Lighten a color by a percentage
 */
export function lightenColor(hex: string, percent: number): string {
  const rgb = hexToRgb(hex)
  if (!rgb) return hex

  const amount = percent / 100
  const r = Math.min(255, Math.round(rgb.r + (255 - rgb.r) * amount))
  const g = Math.min(255, Math.round(rgb.g + (255 - rgb.g) * amount))
  const b = Math.min(255, Math.round(rgb.b + (255 - rgb.b) * amount))

  return rgbToHex(r, g, b)
}

/**
 * Darken a color by a percentage
 */
export function darkenColor(hex: string, percent: number): string {
  const rgb = hexToRgb(hex)
  if (!rgb) return hex

  const amount = percent / 100
  const r = Math.max(0, Math.round(rgb.r * (1 - amount)))
  const g = Math.max(0, Math.round(rgb.g * (1 - amount)))
  const b = Math.max(0, Math.round(rgb.b * (1 - amount)))

  return rgbToHex(r, g, b)
}

/**
 * Add opacity to a hex color (returns rgba string)
 */
export function addOpacity(hex: string, opacity: number): string {
  const rgb = hexToRgb(hex)
  if (!rgb) return hex

  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`
}

/**
 * Get business color with fallback
 */
export function getBusinessColor(color?: string, fallback: string = '#1E293B'): string {
  return color || fallback
}

/**
 * Generate color variants for a business color
 */
export function getColorVariants(baseColor: string) {
  const color = getBusinessColor(baseColor)
  
  return {
    base: color,
    light: lightenColor(color, 20),
    lighter: lightenColor(color, 40),
    lightest: lightenColor(color, 60),
    dark: darkenColor(color, 10),
    darker: darkenColor(color, 20),
    darkest: darkenColor(color, 30),
    // Opacity variants
    opacity10: addOpacity(color, 0.1),
    opacity15: addOpacity(color, 0.15),
    opacity20: addOpacity(color, 0.2),
    opacity25: addOpacity(color, 0.25),
    opacity50: addOpacity(color, 0.5),
  }
}

